import React, { useEffect, useMemo } from "react";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import PdfAssetViewer from "../../components/PdfAssetViewer";
import { useQuery } from "@apollo/client/react";
import { useI18n } from "../../hooks/i18n-hooks";
import apolloClient from "../../configs/graphql.config";
import { useSnackbar } from "../../hooks/snackbar";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { FIND_MY_INVOICES, GET_INVOICE } from "../../graphql/invoices";
import { isScrollEnabledForPdfComponent } from "../../utils/pdf-scroll";

type InvoiceDetailsScreenProps = StackScreenProps<RootStackParamList, "InvoiceDetails">;

const InvoiceDetailsScreen = ({ route, navigation }: InvoiceDetailsScreenProps) => {
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();

    const { invoiceId } = useMemo(() => route.params, [route.params]);
    const { data, error, loading } = useQuery(GET_INVOICE, {
        variables: { invoiceId },
        skip: !invoiceId,
    });

    const invoice = data?.getInvoice;
    const assetId = invoice?.invoiceAsset?.id || "";
    const filename = useMemo(() => {
        if (!invoice) {
            return "";
        }
        const invoiceStartDate = new Date(invoice.fromDate);
        return i18n.t("screens.user.invoices.filename_invoice", {
            month: (invoiceStartDate.getMonth() + 1).toLocaleString(i18n.locale, {
                minimumIntegerDigits: 2,
                useGrouping: false,
            }),
            year: invoiceStartDate.getFullYear(),
            workerName: invoice.missionInfo.workerInfo?.name || "",
        });
    }, [i18n, invoice]);

    useEffect(() => {
        // check if invoice still exists,
        // backend return null if invoice does not exist or is not accessible by the current user
        if (data && data.getInvoice === null) {
            void apolloClient.refetchQueries({
                include: [FIND_MY_INVOICES],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`invoice.invoice_not_found`),
                mode: "warning",
            });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.reset({
                    index: 0,
                    routes: [{ name: "Home" }],
                });
            }
        }
    }, [data, i18n, navigation, showSnackbar]);
    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            <ScreenCard
                isContentContainerFlex
                scrollEnabled={isScrollEnabledForPdfComponent("ScreenCard")}
            >
                {assetId && (
                    <PdfAssetViewer
                        assetId={assetId}
                        filename={filename}
                        scrollEnabled={isScrollEnabledForPdfComponent("PdfViewer")}
                        waitingDescription={i18n.t("screens.user.invoices.wait_invoice_asset")}
                    />
                )}
            </ScreenCard>
        </LoadingBoundary>
    );
};

export default InvoiceDetailsScreen;
